/**
 * Canonical vehicle display helper — sw-ev-user.
 *
 * The user-facing app should identify vehicles by their actual
 * make + model + (year) — the nickname (called `displayName` in
 * the user-vehicles DTO) is a friendly label but not a substitute
 * for the vehicle's identity. Screens that previously showed the
 * nickname as the primary identity leave a car labelled "Wife's
 * car" indistinguishable from any other unnamed row.
 *
 * Three helpers, same behaviour as sw-ev-admin's canonical util:
 *
 *   buildVehicleDisplay({ make, model, year })
 *     Pure local computer. Byte-for-byte format match with the
 *     sw-ev-api helper `services/vehicles/vehicleDisplay.js`.
 *
 *   vehicleDisplayFor(vehicle)
 *     Priority:
 *       1. `vehicle.displayVehicle` (server-computed, when the API
 *          payload is from a post-rollout deployment)
 *       2. local `buildVehicleDisplay(make, model, year)`
 *       3. 'Vehicle details unavailable'
 *     Nickname is NEVER a fallback — that would silently
 *     reintroduce the exact bug we're eliminating.
 *
 *   buildVehicleDisplayFields(vehicle)
 *     Returns { primary, nickname, color } for multi-line cards.
 *     Primary is `vehicleDisplayFor(vehicle)`; nickname is the
 *     labelled secondary (or null when it would duplicate the
 *     primary); color is the raw string (or null).
 *
 * Note on nickname field name: the user-vehicles DTO calls the
 * nickname `displayName` (established convention). We treat both
 * `displayName` and `vehicleName` and `nickname` as valid nickname
 * sources so this helper works against every payload shape.
 */

const FALLBACK = 'Vehicle details unavailable';
const NICKNAME_PREFIX = 'Nickname: ';

function trimString(value) {
  if (typeof value !== 'string') return '';
  return value.trim();
}

function normaliseYear(value) {
  if (value == null || value === '') return '';
  return String(value).trim();
}

/**
 * @param {{ make?: string|null, model?: string|null, year?: number|string|null }} input
 * @returns {string}
 */
export function buildVehicleDisplay(input) {
  const src = input || {};
  const make = trimString(src.make);
  const model = trimString(src.model);
  const year = normaliseYear(src.year);

  const hasMake = make.length > 0;
  const hasModel = model.length > 0;

  if (!hasMake && !hasModel) return FALLBACK;

  const parts = [];
  if (hasMake) parts.push(make);
  if (hasModel) parts.push(model);
  const base = parts.join(' ');
  return year ? `${base} (${year})` : base;
}

/**
 * Pick the nickname from whichever key the payload happens to use.
 * The user-vehicles DTO uses `displayName`; other shapes may use
 * `nickname` or the legacy `vehicleName`. Preference order matches
 * the field-canonicalisation direction (canonical alias first).
 */
function pickNickname(src) {
  return (
    trimString(src?.nickname)
    || trimString(src?.displayName)
    || trimString(src?.vehicleName)
  );
}

/**
 * Resolve the vehicle display string for a DTO. UX priority:
 *
 *   1. `vehicle.displayVehicle` (server-computed) — but only when
 *      it isn't the semantic fallback string itself.
 *   2. locally computed `buildVehicleDisplay(make, model, year)`.
 *   3. the vehicle nickname (last-ditch friendly label so the row
 *      isn't dead when only the nickname is known).
 *   4. `'Vehicle details unavailable'` — genuine unknowns only.
 *
 * @param {object|null|undefined} vehicle
 * @returns {string}
 */
export function vehicleDisplayFor(vehicle) {
  const src = vehicle || {};

  const server = trimString(src.displayVehicle);
  if (server && server !== FALLBACK) return server;

  const localCompute = buildVehicleDisplay(src);
  if (localCompute !== FALLBACK) return localCompute;

  const nickname = pickNickname(src);
  if (nickname) return nickname;

  return FALLBACK;
}

/**
 * Compose the multi-line cell shape. Primary uses
 * `vehicleDisplayFor` (which already handles the nickname
 * promotion when make/model are missing). Secondary is a labelled
 * "Nickname: X" line, or null when the nickname is absent or is
 * the same as the primary.
 *
 * @param {object|null|undefined} vehicle
 * @returns {{ primary: string, nickname: string|null, color: string|null }}
 */
export function buildVehicleDisplayFields(vehicle) {
  const src = vehicle || {};
  const primary = vehicleDisplayFor(src);
  const nickname = pickNickname(src);
  const color = trimString(src.color);

  const nicknameLine =
    nickname && nickname !== primary ? `${NICKNAME_PREFIX}${nickname}` : null;

  return {
    primary,
    nickname: nicknameLine,
    color: color.length > 0 ? color : null,
  };
}

export const VEHICLE_DISPLAY_FALLBACK = FALLBACK;
